import React from 'react';
import { Box, Button, Grid, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useViewContext } from '../context/ViewContext';
import { useAuth } from '../auth/AuthContext';
import CustomerSelector from './CustomerSelector';
import FleetSelector from './FleetSelector';
import CanopySelector from './CanopySelector';
import ServiceStatusIndicator from './ServiceStatusIndicator';

/**
 * TopBar component holds the customer, fleet, and canopy selection components.
 * The canopy selector is hidden in fleet view and browser view since both have their own selection mechanisms.
 * On mobile:
 *  - Non-superusers skip the customer selector (auto-scoped via auth.customerId)
 *  - Selectors stack vertically
 *  - ServiceStatusIndicator is hidden to save space
 */
function TopBar() {
  const { currentView } = useViewContext();
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const accountViews = ['dashboard', 'bessView', 'emsView', 'loadView', 'pccView', 'energyReports', 'settings'];
  // Account-level views select assets within the page and do not need a canopy selector.
  const showCanopySelector = currentView !== 'fleetView' && currentView !== 'browserView' && !accountViews.includes(currentView);
  // On mobile, only show customer selector for superusers
  const showCustomerSelector = auth.isSuperuser;

  return (
    <Paper 
      elevation={2}
      sx={{ 
        p: isMobile ? 1.5 : 2, 
        borderBottom: 1, 
        borderColor: 'divider', 
        bgcolor: 'background.paper' 
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Signed in as
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : undefined }}>
            {auth.user?.email || 'unknown'}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={auth.signOut}>
          Sign out
        </Button>
      </Stack>
      <Grid container spacing={isMobile ? 1.5 : 3} alignItems="flex-start">
        {/* Customer Selector — hidden for non-superusers on mobile */}
        {showCustomerSelector ? (
          <Grid item xs={12} md={showCanopySelector ? 4 : 6}>
            <CustomerSelector />
          </Grid>
        ) : null}

        {/* Fleet Selector */}
        <Grid item xs={12} md={showCanopySelector ? (showCustomerSelector ? 4 : 6) : (showCustomerSelector ? 6 : 12)}>
          <FleetSelector />
        </Grid>

        {/* Canopy Selector - hidden in fleet view and browser view */}
        {showCanopySelector && (
          <Grid item xs={12} md={4}>
            <CanopySelector />
          </Grid>
        )}
      </Grid>
      {/* Service Status Indicators — hidden on mobile to save space */}
      {!isMobile && (
        <Box sx={{ mt: 2, ml: 1 }}>
          <ServiceStatusIndicator />
        </Box>
      )}
    </Paper>
  );
}

export default TopBar;
